import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { User, Heart, AlertTriangle, ArrowRight, LayoutDashboard } from 'lucide-react';
import Logo from '../assets/images/arka-saydam-logo.png';

const PartnerRegistration = ({ isDarkMode }) => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [inviter, setInviter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(1);

    // Form Data
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        password: '',
        dietaryPreference: '',
        sensitivities: ''
    });

    useEffect(() => {
        const validateInvite = async () => {
            try {
                const response = await axios.get(`/api/profile/partner/invite/${token}`);
                setInviter(response.data.inviter);
                // Pre-fill email if available in invite
                if (response.data.inviteEmail) {
                    setFormData(prev => ({ ...prev, email: response.data.inviteEmail }));
                }
            } catch (error) {
                toast.error('Geçersiz veya süresi dolmuş davet.');
                navigate('/');
            } finally {
                setLoading(false);
            }
        };
        validateInvite();
    }, [token, navigate]);

    const handleRegister = async () => {
        try {
            setLoading(true);
            const response = await axios.post('/api/profile/partner/register', {
                token,
                ...formData
            });

            localStorage.setItem('authToken', response.data.token);
            toast.success('Kaydınız başarıyla tamamlandı, şefim!');
            navigate('/partner/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Kayıt sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    if (loading && step === 1) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4">
            {/* Logo */}
            <div className="mb-8 text-center">
                <img src={Logo} alt="Logo" className="h-16 mx-auto mb-2" />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bana Bir Tarif Ver</h1>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-lg w-full overflow-hidden border border-gray-100 dark:border-slate-800">
                {/* Progress Bar */}
                <div className="h-2 w-full bg-gray-100 dark:bg-slate-800">
                    <div
                        className="h-full bg-blue-500 transition-all duration-500"
                        style={{ width: `${(step / 3) * 100}%` }}
                    ></div>
                </div>

                <div className="p-8">
                    {step === 1 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="text-center mb-8">
                                <span className="inline-block p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 mb-4">
                                    <User size={32} />
                                </span>
                                <h2 className="text-2xl font-bold dark:text-white">Tanışalım Şefim!</h2>
                                <p className="text-gray-500 dark:text-gray-400 mt-2">
                                    {inviter?.name} sizi mutfağına davet etti.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium dark:text-gray-300">Ad</label>
                                    <input
                                        type="text"
                                        placeholder="Adınız"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium dark:text-gray-300">Soyad</label>
                                    <input
                                        type="text"
                                        placeholder="Soyadınız"
                                        value={formData.surname}
                                        onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium dark:text-gray-300">E-posta</label>
                                <input
                                    type="email"
                                    placeholder="E-posta adresiniz"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium dark:text-gray-300">Şifre Oluştur</label>
                                <input
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                />
                            </div>

                            <button
                                onClick={() => setStep(2)}
                                disabled={!formData.name || !formData.email || !formData.password}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-500/30 transition flex items-center justify-center gap-2 group"
                            >
                                Devam Et
                                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="text-center mb-8">
                                <span className="inline-block p-3 bg-green-100 dark:bg-green-900/30 rounded-full text-green-600 mb-4">
                                    <Heart size={32} />
                                </span>
                                <h2 className="text-2xl font-bold dark:text-white">Beslenme Tercihiniz?</h2>
                                <p className="text-gray-500 dark:text-gray-400 mt-2">
                                    Size daha doğru tarifler önerebilmemiz için önemli.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                                {['Normal', 'Vegan', 'Vejetaryen', 'Ketojenik', 'Glutensiz'].map((pref) => (
                                    <button
                                        key={pref}
                                        onClick={() => setFormData({ ...formData, dietaryPreference: pref })}
                                        className={`p-4 rounded-xl border-2 text-left transition flex justify-between items-center ${formData.dietaryPreference === pref
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                                : 'border-gray-100 dark:border-slate-800 hover:border-gray-200 dark:hover:border-slate-700 dark:text-gray-300'
                                            }`}
                                    >
                                        <span className="font-medium">{pref}</span>
                                        {formData.dietaryPreference === pref && <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">✓</div>}
                                    </button>
                                ))}
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setStep(1)}
                                    className="flex-1 py-4 text-gray-500 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition"
                                >
                                    Geri
                                </button>
                                <button
                                    onClick={() => setStep(3)}
                                    disabled={!formData.dietaryPreference}
                                    className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg transition"
                                >
                                    Son Adım
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="text-center mb-8">
                                <span className="inline-block p-3 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 mb-4">
                                    <AlertTriangle size={32} />
                                </span>
                                <h2 className="text-2xl font-bold dark:text-white">Gıda Hassasiyetiniz Var mı?</h2>
                                <p className="text-gray-500 dark:text-gray-400 mt-2">
                                    Alerjiniz olan veya yemediğiniz özel ürünler.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <textarea
                                    placeholder="Örn: Gluten, Laktoz, Yer fıstığı..."
                                    value={formData.sensitivities}
                                    onChange={(e) => setFormData({ ...formData, sensitivities: e.target.value })}
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white resize-none"
                                ></textarea>
                                <p className="text-xs text-gray-400 italic">Birden fazla ise virgül ile ayırabilirsiniz.</p>
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setStep(2)}
                                    className="flex-1 py-4 text-gray-500 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition"
                                >
                                    Geri
                                </button>
                                <button
                                    onClick={handleRegister}
                                    disabled={loading}
                                    className="flex-[2] bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-xl shadow-lg transition flex items-center justify-center gap-2"
                                >
                                    {loading ? 'Kaydediliyor...' : 'Sisteme Kayıt Ol'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Branding */}
            <div className="mt-8 flex items-center gap-2 text-gray-400 text-sm">
                <LayoutDashboard size={16} />
                <span>Bana Bir Tarif Ver • Yardımcı Şef Onboarding</span>
            </div>
        </div>
    );
};

export default PartnerRegistration;
